import React, { ReactNode, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import styled, { useTheme } from 'styled-components'
import { FitpetButton } from '@/components/common/buttons/fitpet-button'
import { Typo, TypoVariant } from '@/components/common/typography'
import { RootState } from '@/stores/store'
import Modal from './Modal'

export type SimpleModalProps = {
  title: string | ReactNode
  description?: string | ReactNode
  onOk?: () => void
  okText?: string
  useCancel?: boolean
  onCancel?: () => void
  initialVisible?: boolean
  children?: ReactNode
  onAfterClose?: () => void
}

const SimpleModal = ({
  title,
  description = '',
  onOk,
  okText = '확인',
  useCancel = true,
  onCancel,
  initialVisible = false,
  onAfterClose,
  children,
}: SimpleModalProps) => {
  const [open, setOpen] = useState(initialVisible)
  const { spacing, fontWeight, color } = useTheme()
  const bottomSheetData = useSelector((rootState: RootState) => rootState.reduxDataReducers.bottomSheetData)
  const isAnyVisible = Object.values(bottomSheetData).some((visible) => visible)

  const showModal = () => setOpen(true)
  const hideModal = () => {
    onAfterClose?.()
    setOpen(false)
  }

  const handleOk = () => {
    onOk?.()
    hideModal()
  }

  const handleCancel = () => {
    onCancel?.()
    hideModal()
  }

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        document.body.style.overflow = 'hidden'
      })
    } else {
      if (isAnyVisible) return
      setTimeout(() => {
        document.body.style.overflow = 'unset'
      })
    }
    return () => {
      if (isAnyVisible) return
      document.body.style.overflow = 'unset'
    }
  }, [open, isAnyVisible])

  if (!open) return null

  return (
    <Modal open={open}>
      <Modal.Content>
        <Modal.Title>
          <div>
            {typeof title === 'string' ? (
              title.split('\n').map((words, index) => (
                <Typo variant={TypoVariant.Heading6Bold} key={index} color={color.gray900}>
                  {words}
                </Typo>
              ))
            ) : (
              <>{title}</>
            )}
          </div>
        </Modal.Title>
        {!!description && (
          <Modal.SubTitle>
            <StyledSubTitleContent>
              {typeof description === 'string' ? (
                description.split('\n').map((words, index) => (
                  <Typo variant={TypoVariant.Body3Regular} key={index} color={color.gray700}>
                    {words}
                  </Typo>
                ))
              ) : (
                <>{description}</>
              )}
            </StyledSubTitleContent>
          </Modal.SubTitle>
        )}
      </Modal.Content>
      <Modal.Footer>
        <StyledFooterContainer>
          <FitpetButton width="100%" onClick={handleOk}>
            <FitpetButton.ButtonText>{okText}</FitpetButton.ButtonText>
          </FitpetButton>
          {useCancel && (
            <TouchArea onClick={handleCancel}>
              <Typo variant={TypoVariant.Body1Regular} color={color.gray500}>
                취소
              </Typo>
            </TouchArea>
          )}
        </StyledFooterContainer>
      </Modal.Footer>
    </Modal>
  )
}

const StyledSubTitleContent = styled.div`
  display: flex;
  flex-direction: column;
`

const TouchArea = styled.div`
  padding: 1.2rem 2rem;
`

const StyledFooterContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.8rem;
`

export default SimpleModal
